import { BaseEntity, Status } from './common';

export type Gender = 'MALE' | 'FEMALE' | 'OTHER';
export type ActivityLevel = 'SEDENTARY' | 'LIGHT' | 'MODERATE' | 'ACTIVE' | 'VERY_ACTIVE';
export type ClientStatus = 'PENDING' | 'ACTIVE' | 'FROZEN' | 'STOPPED' | 'INACTIVE' | 'SUSPENDED';

export interface ClientAddress extends BaseEntity {
  clientId: string;
  fullAddress: string;
  isPrimary: boolean;
  countryId?: string;
  cityId?: string;
  notes?: string;
}

export interface Client extends BaseEntity {
  firstName: string;
  lastName: string;
  fatherName?: string;
  motherFullName?: string;
  email?: string;
  phone?: string; // legacy UI field
  mobilePhone?: string; // server field
  address?: string;
  cityId?: string; // legacy UI field
  locationId?: string; // server field
  dateOfBirth?: string;
  gender?: Gender;
  age?: number;
  height?: number;
  weight?: number;
  activityLevel?: ActivityLevel;
  bmi?: number;
  ibw?: number;
  ubw?: number;
  calories?: number;
  bmiStatus?: string;
  weightChange?: number;
  medicalConditions?: string;
  allergies?: string;
  dietaryPreferences?: string;
  medicalHistory?: string;
  medications?: string;
  vitaminDeficiencies?: string;
  counterMedications?: string;
  previousDiet?: string;
  lastDietDate?: string;
  weightLost?: number;
  smoking?: boolean;
  alcohol?: boolean;
  allergiesHistory?: string;
  foodDislike?: string;
  eatingDisorder?: boolean;
  status: ClientStatus;
  statusDate: string;
  city?: {
    id: string;
    name: string;
    country?: {
      id: string;
      name: string;
    };
  };
  // server includes relations on some endpoints
  nationality?: { id: string; name: string; code?: string };
  location?: { id: string; name: string; country?: { id: string; name: string } };
  diet?: { id: string; name: string };
  payments?: Payment[];
  addresses?: ClientAddress[];
  _count?: {
    payments: number;
  };
}

export interface Payment extends BaseEntity {
  clientId: string;
  amount: number;
  paymentDate: string;
  status: Status;
  statusDate: string;
}

export interface City extends BaseEntity {
  name: string;
  countryId: string;
  status: Status;
  statusDate: string;
  country?: {
    id: string;
    name: string;
  };
}

export interface Country extends BaseEntity {
  name: string;
  code: string;
  phonePrefix?: string;
  zipCodeFormat?: string;
  status: Status;
  statusDate: string;
}

// Request types (align with server schema)
export interface CreateClientRequest {
  firstName: string;
  fatherName?: string;
  motherFullName?: string;
  lastName: string;
  email?: string;
  mobilePhone: string;
  nationalityId: string;
  locationId: string; // City
  routeDeliveryId?: string;
  dietId: string;
  priceId?: string;
  delivery?: boolean; // default true

  // Addresses (optional)
  addresses?: Array<
    | string
    | {
        fullAddress: string;
        isPrimary?: boolean;
        countryId?: string;
        cityId?: string;
        notes?: string;
      }
  >;

  // Health Metrics
  dateOfBirth?: string;
  gender?: Gender;
  age?: number;
  height?: number; // in cm
  weight?: number; // in kg
  activityLevel?: ActivityLevel;

  // Calculated Health Metrics (auto-calculated)
  bmi?: number;
  ibw?: number;
  ubw?: number;
  calories?: number;
  bmiStatus?: string;
  weightChange?: number;

  // Medical Information
  medicalConditions?: string;
  allergies?: string;
  dietaryPreferences?: string;
  medicalHistory?: string;
  medications?: string;
  vitaminDeficiencies?: string;
  counterMedications?: string;

  // Diet History
  previousDiet?: string;
  lastDietDate?: string;
  weightLost?: number;

  // General Assessment
  smoking?: boolean;
  alcohol?: boolean;
  allergiesHistory?: string;
  foodDislike?: string;
  eatingDisorder?: boolean;
}

export interface UpdateClientRequest {
  firstName?: string;
  fatherName?: string;
  motherFullName?: string;
  lastName?: string;
  email?: string;
  mobilePhone?: string;
  nationalityId?: string;
  locationId?: string;
  routeDeliveryId?: string;
  dietId?: string;
  priceId?: string;
  delivery?: boolean;
  status?: ClientStatus;

  // Addresses (optional)
  addresses?: Array<
    | string
    | {
        fullAddress: string;
        isPrimary?: boolean;
        countryId?: string;
        cityId?: string;
        notes?: string;
      }
  >;

  // Health Metrics
  dateOfBirth?: string;
  gender?: Gender;
  height?: number;
  weight?: number;
  activityLevel?: ActivityLevel;

  // Calculated Health Metrics
  bmi?: number;
  ibw?: number;
  ubw?: number;
  calories?: number;

  // Medical Information
  medicalConditions?: string;
  allergies?: string;
  dietaryPreferences?: string;
}

// Country & City request types
export interface CreateCountryRequest { name: string; code: string; status?: Status; }
export interface UpdateCountryRequest { name?: string; code?: string; status?: Status; }

export interface CreateCityRequest { name: string; countryId: string; status?: Status; }
export interface UpdateCityRequest { name?: string; countryId?: string; status?: Status; }


// Response types
export interface ClientsResponse {
  clients: Client[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

export interface CitiesResponse {
  cities: City[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

export interface CountriesResponse {
  countries: Country[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

// Helper functions
export const getFullName = (client: Client): string => {
  return `${client.firstName} ${client.lastName}`;
};

export const getAge = (dateOfBirth?: string): number | null => {
  if (!dateOfBirth) return null;
  const today = new Date();
  const birthDate = new Date(dateOfBirth);
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();

  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }

  return age;
};

export const getBMI = (height?: number, weight?: number): number | null => {
  if (!height || !weight) return null;
  const heightInMeters = height / 100;
  return Math.round((weight / (heightInMeters * heightInMeters)) * 10) / 10;
};

export const getActivityLevelLabel = (level?: ActivityLevel): string => {
  const labels: Record<ActivityLevel, string> = {
    SEDENTARY: 'Sedentary',
    LIGHT: 'Light Activity',
    MODERATE: 'Moderate Activity',
    ACTIVE: 'Active',
    VERY_ACTIVE: 'Very Active',
  };
  return level ? labels[level] : 'Not specified';
};

export const getGenderLabel = (gender?: Gender): string => {
  const labels: Record<Gender, string> = {
    MALE: 'Male',
    FEMALE: 'Female',
    OTHER: 'Other',
  };
  return gender ? labels[gender] : 'Not specified';
};

// Ideal Body Weight (Devine Formula)
export const getIBW = (height?: number, gender?: Gender): number | null => {
  if (!height) return null;
  const heightInInches = height / 2.54;

  if (gender === 'MALE') {
    return Math.round((50 + 2.3 * (heightInInches - 60)) * 10) / 10;
  } else if (gender === 'FEMALE') {
    return Math.round((45.5 + 2.3 * (heightInInches - 60)) * 10) / 10;
  }
  return null;
};

// Usual Body Weight (assuming current weight is UBW if not specified)
export const getUBW = (weight?: number): number | null => {
  return weight ? Math.round(weight * 10) / 10 : null;
};

// Daily Caloric Needs (Harris-Benedict Equation)
export const getCalories = (
  weight?: number,
  height?: number,
  dateOfBirth?: string,
  gender?: Gender,
  activityLevel?: ActivityLevel
): number | null => {
  if (!weight || !height || !dateOfBirth || !gender) return null;

  // Calculate age
  const today = new Date();
  const birthDate = new Date(dateOfBirth);
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }

  // Harris-Benedict BMR calculation
  let bmr: number;
  if (gender === 'MALE') {
    bmr = 88.362 + (13.397 * weight) + (4.799 * height) - (5.677 * age);
  } else {
    bmr = 447.593 + (9.247 * weight) + (3.098 * height) - (4.330 * age);
  }

  // Activity multiplier
  const activityMultipliers: Record<ActivityLevel, number> = {
    SEDENTARY: 1.2,
    LIGHT: 1.375,
    MODERATE: 1.55,
    ACTIVE: 1.725,
    VERY_ACTIVE: 1.9,
  };

  const multiplier = activityMultipliers[activityLevel || 'MODERATE'] || 1.55;
  return Math.round(bmr * multiplier);
};

export const getClientStatusColor = (status: ClientStatus): string => {
  const colors: Record<ClientStatus, string> = {
    PENDING: 'bg-yellow-100 text-yellow-800',
    ACTIVE: 'bg-green-100 text-green-800',
    FROZEN: 'bg-blue-100 text-blue-800',
    STOPPED: 'bg-orange-100 text-orange-800',
    INACTIVE: 'bg-gray-100 text-gray-800',
    SUSPENDED: 'bg-red-100 text-red-800',
  } as any;
  return colors[status] || 'bg-gray-100 text-gray-800';
};
